import React, { useCallback, useRef } from "react";
import { Nav, NavDropdown } from "react-bootstrap";
import AsyncSelect from "react-select/async";
import debounce from "lodash/debounce";
import { useHistory, useLocation } from "react-router-dom";
import { supportAutocompleteUsers } from "../../../../docuclipper/api";

interface UserOption {
  value: number;
  label: string;
  email: string;
  effectiveEmail: string;
  isSeat?: boolean;
}

const NAV_ITEMS: { key: string; label: string; path: string }[] = [
  { key: "home", label: "Home", path: "/support" },
  { key: "trials", label: "Trials", path: "/support/trials" },
  { key: "cancellations", label: "Cancellations", path: "/support/cancellations" },
  { key: "churned", label: "Churned", path: "/support/churned" }
];

const TOOLS: { key: string; label: string; path: string }[] = [
  { key: "csm", label: "CSM activations", path: "/support/admin/csm" },
  { key: "ai-chat", label: "AI chat review", path: "/support/admin/ai-chat" },
  { key: "faq", label: "FAQ management", path: "/support/admin/faq" },
  { key: "guardrails", label: "Guardrails", path: "/support/admin/guardrails" },
  { key: "prospects", label: "Prospect CSV", path: "/support/admin/prospects" }
];

const SupportShell: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const history = useHistory();
  const location = useLocation();

  const fetchUsers = async (q: string): Promise<UserOption[]> => {
    if (!q || q.trim().length < 2) return [];
    try {
      const rsp = await supportAutocompleteUsers({ q, limit: 10 });
      return rsp.users.map((u: any) => {
        const name = `${u.firstName || ""} ${u.lastName || ""}`.trim();
        const tag = u.isSeat ? ` — SEAT${u.admin ? ` of ${u.admin.email}` : ""}` : "";
        return {
          value: u.id,
          label: `${u.email}${name ? ` (${name})` : ""}${tag}`,
          email: u.email,
          effectiveEmail: u.isSeat && u.admin ? u.admin.email : u.email,
          isSeat: !!u.isSeat
        };
      });
    } catch {
      return [];
    }
  };

  const debounced = useRef(
    debounce((q: string, cb: (opts: UserOption[]) => void) => {
      fetchUsers(q).then(cb);
    }, 300)
  ).current;

  const loadUsers = useCallback(
    (q: string, cb: (opts: UserOption[]) => void) => debounced(q, cb),
    [debounced]
  );

  const onSelect = (opt: UserOption | null) => {
    if (!opt) return;
    history.push(`/support/c/${encodeURIComponent(opt.effectiveEmail)}`);
  };

  const isActive = (path: string) => {
    if (path === "/support") return location.pathname === "/support";
    return location.pathname.startsWith(path);
  };

  return (
    <div style={{ minHeight: "100vh" }}>
      <div
        style={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          background: "#fff",
          borderBottom: "1px solid #e5e7eb",
          padding: "10px 20px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.03)"
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 24, maxWidth: 1500, margin: "0 auto" }}>
          <div style={{ fontWeight: 700, fontSize: 16, cursor: "pointer" }} onClick={() => history.push("/support")}>
            Support
          </div>
          <div style={{ minWidth: 360, flex: "0 1 460px" }}>
            <AsyncSelect
              cacheOptions
              loadOptions={loadUsers}
              onChange={onSelect as any}
              value={null}
              placeholder="Search customer by email or name…"
              isClearable
            />
          </div>
          <Nav variant="pills" style={{ gap: 4 }}>
            {NAV_ITEMS.map(n => (
              <Nav.Item key={n.key}>
                <Nav.Link
                  active={isActive(n.path)}
                  onClick={() => history.push(n.path)}
                  style={{ padding: "4px 12px", cursor: "pointer" }}
                >
                  {n.label}
                </Nav.Link>
              </Nav.Item>
            ))}
            <NavDropdown
              title="Admin tools"
              id="support-admin-tools"
              active={location.pathname.startsWith("/support/admin/")}
            >
              {TOOLS.map(t => (
                <NavDropdown.Item key={t.key} onClick={() => history.push(t.path)}>
                  {t.label}
                </NavDropdown.Item>
              ))}
            </NavDropdown>
          </Nav>
        </div>
      </div>
      <div style={{ maxWidth: 1500, margin: "0 auto", padding: 20 }}>{children}</div>
    </div>
  );
};

export default SupportShell;
